import React, { useMemo } from "react";
import { ColumnDef, SortingState } from "@tanstack/react-table";
import { CreateContract, VisibleColumnSpec } from "@/types";
import { useTranslation } from "react-i18next";
import NonSortableHeader from "@/components/molecules/NonSortableHeader";
import { Group } from "@mantine/core";
import styles from "./styles.module.css";
import dayjs from "dayjs";
import { useAuth } from "@/contexts/AuthContext";
import DataTable from "@/components/molecules/DataTable";
import CTA from "@/components/atoms/CTA";
import { useVisibleColumns } from "@/hooks/useVisibleColumns";
import { useGetContractTemplates } from "@/services/Contracts";
import { useGetCreateOrderValues } from "@/services/Orders";
import { useGetCompanySpecificProducts } from "@/services/Company";
import { convertAmountToAmountUnit } from "@/utils/ConvertAmountToAmountUnit";

interface ContractTemplatesTableProps {
  /** Define a column spec of what rows and columns to display and when **/
  visibleColumns?: VisibleColumnSpec<CreateContract, "actions">[];
  /** Define a predefined sort order **/
  initialSort?: SortingState;
  /** Whether to show client-side pagination */
  pagination?: boolean;
  /** Decide whether to enable or disable actions **/
  actionable?: boolean;
  /** Optional rows per page for client-side pagination */
  initialRowsPerPage?: number;
}

/**
 * A reusable task table component, with sorting, filtering, actions and pagination.
 * @param ContractTemplatesTableProps - Component props
 * @param visibleColumns - Constant Array defined to manage which columns to display and when
 * @param initialSort - Define a predefined sort order
 * @param pagination - Boolean to enable or disable pagination
 * @param initialRowsPerPage - set maximum number of rows to display used for snapshots
 *
 **/
const ContractTemplatesTable: React.FC<ContractTemplatesTableProps> = ({
  visibleColumns,
  initialSort = null,
  pagination = true,
  actionable: _actionable = true,
  initialRowsPerPage = 15,
}) => {
  const { currentCompany } = useAuth();
  const { data: companySpecificProducts, isLoading: isLoadingCompanyProducts } = useGetCompanySpecificProducts(
    currentCompany?.companyId as number,
    true,
    {
      refetchOnWindowFocus: false,
      enabled: !!currentCompany?.companyId,
    }
  );

  const { data: templates, isLoading } = useGetContractTemplates(currentCompany?.companyId as number, {
    refetchOnWindowFocus: false,
    enabled: !!currentCompany?.companyId,
  });

  const { data: orderValues, isLoading: isLoadingOrderValues } = useGetCreateOrderValues();

  const { t } = useTranslation();

  const baseColumns: ColumnDef<CreateContract>[] = useMemo(
    () => [
      {
        header: ({ column }) => <NonSortableHeader column={column} label={t("settings:templates.templateName")} />,
        id: "templateName",
        cell: ({ row }) => {
          return row.original?.templateName ?? row.original?.name ?? "";
        },
      },
      {
        header: ({ column }) => <NonSortableHeader column={column} label={t("product:product")} />,
        id: "type",
        cell: ({ row }) => {
          const product = companySpecificProducts?.find((p) => p.id === row.original.companySpecificProductId);
          if (!product) return "";

          const parts: string[] = [];
          product.properties.map((prop) => {
            parts.push(prop.value);
          });

          const details = parts.join(", ");
          return product.companySpecificProductName + (details ? ` (${details})` : "");
        },
      },
      {
        header: ({ column }) => <NonSortableHeader column={column} label={t("common:type")} />,
        id: "type",
        cell: ({ row }) => {
          const isBuy = row.original.contractBuyers.find((buyer) => buyer === currentCompany?.companyId);
          const product = companySpecificProducts?.find(
            (product) => product.id === row.original.companySpecificProductId
          );

          if (row.original.contractEndDate !== null) {
            const buyOrSell = isBuy ? "settings:templates.tradeByDateBuy" : "settings:templates.tradeByDateSell";
            return t(buyOrSell) + ` (${dayjs(row.original.contractEndDate).format("YYYY-MM-DD")})`;
          } else {
            const measureUnit = orderValues?.units?.find((unit) => unit.id === row.original.priceUnitId);
            const buyOrSell = isBuy ? "settings:templates.tradeByAmountBuy" : "settings:templates.tradeByAmountSell";
            return (
              t(buyOrSell) +
              ` (${convertAmountToAmountUnit(row.original.amount || 0, row.original.priceUnitId, product?.kgPerMeter || 0)} ${measureUnit?.value ? measureUnit?.value : ""})`
            );
          }
        },
      },
      {
        // NEVER SHOWS ON PURPOSE
        id: "actions",
        header: () => "",
        meta: {
          breakpoint: "mantine-hidden-from-xs",
        },
        cell: () => {
          return (
            <CTA type={"button"} intent={"secondary"} onClick={() => {}}>
              {t("actions:showMore")}
            </CTA>
          );
        },
      },
    ],
    [t, templates, companySpecificProducts]
  );

  const columns = useVisibleColumns<CreateContract, "actions">(baseColumns, visibleColumns);

  return (
    <Group dir={"col"} className={styles.tableContainer}>
      <DataTable
        onRowClick={undefined}
        data={templates || []}
        columns={columns}
        isLoading={isLoading || isLoadingOrderValues || isLoadingCompanyProducts}
        mobileHeaderCell={0}
        showPagination={pagination}
        initialSort={initialSort ?? undefined}
        initialRowsPerPage={initialRowsPerPage}
        actionable={false}
      />
    </Group>
  );
};

export default ContractTemplatesTable;
